import React, { useState, useEffect, useCallback } from 'react';
import { FiLayout, FiImage, FiType, FiCheckCircle, FiSave, FiEye, FiDownload, FiCreditCard, FiGlobe, FiInstagram, FiColumns, FiFileText, FiDroplet } from 'react-icons/fi';
import quotationConfigService from '../../../../services/quotation-config.service';
import Swal from 'sweetalert2';
import './QuotationDesigner.css';

const QuotationDesigner = () => {
    const [config, setConfig] = useState({
        plantilla_base: 'PROFESIONAL',
        color_primario: '#F97316',
        color_secundario: '#333333',
        color_texto: '#333333',
        alineacion_logo: 'LEFT',
        tamano_logo: 'MEDIUM',
        mostrar_firma: false,
        mostrar_branding: true,
        fuente: 'HELVETICA',
        titulo_documento: 'COTIZACIÓN',
        mostrar_sku: true,
        mostrar_impuesto_col: true,
        datos_bancarios: '',
        terminos_condiciones: '',
        instagram: '',
        web: '',
        qr_contenido: '',
        qr_descripcion: '',
        mostrar_qr_virtual: true,
        diseno_pagina: 'NINGUNO',
        marca_agua: '',
        estilo_tabla: 'ESTANDAR',
        texto_footer: '',
        mostrar_label_oferta: true
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await quotationConfigService.getConfig();
            setConfig(data);
            generatePreview(data);
        } catch (err) {
            console.error('Error loading config:', err);
            setError("No se pudo cargar la configuración.");
            // Fallback defaults already in state
        } finally {
            setLoading(false);
        }
    };

    const generatePreview = async (configToPreview) => {
        try {
            setPreviewLoading(true);
            setError(null);
            const blob = await quotationConfigService.getPreview(configToPreview);
            const url = URL.createObjectURL(blob);
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(url);
        } catch (err) {
            console.error('Error generating preview:', err);
            setError("Error al generar vista previa. Verifique su conexión.");
        } finally {
            setPreviewLoading(false);
        }
    };

    // Debounced preview generation would be better here, but let's start simple
    // with a "Refresh Preview" button if auto-generation is too heavy, 
    // or a small timeout.
    useEffect(() => {
        const timer = setTimeout(() => {
            if (!loading) generatePreview(config);
        }, 1000);
        return () => clearTimeout(timer);
    }, [config, loading]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setConfig(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await quotationConfigService.updateConfig(config);
            Swal.fire({
                icon: 'success',
                title: 'Diseño Guardado',
                text: 'El formato de tus cotizaciones ha sido actualizado.',
                timer: 1500,
                showConfirmButton: false
            });
        } catch (err) {
            console.error('Error saving config:', err);
            Swal.fire('Error', 'No se pudo guardar la configuración.', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="designer-loading">Cargando diseñador...</div>;

    return (
        <div className="quotation-designer-container">
            <div className="designer-sidebar">
                <section className="designer-section">
                    <h3><FiLayout /> Plantilla Base</h3>
                    <div className="template-grid">
                        <div
                            className={`template-option ${config.plantilla_base === 'CLASICA' ? 'active' : ''}`}
                            onClick={() => setConfig({ ...config, plantilla_base: 'CLASICA' })}
                        >
                            <div className="template-preview t-classic"></div>
                            <span>Clásica</span>
                        </div>
                        <div
                            className={`template-option ${config.plantilla_base === 'PROFESIONAL' ? 'active' : ''}`}
                            onClick={() => setConfig({ ...config, plantilla_base: 'PROFESIONAL' })}
                        >
                            <div className="template-preview t-pro"></div>
                            <span>Profesional</span>
                        </div>
                        <div
                            className={`template-option ${config.plantilla_base === 'MODERNA' ? 'active' : ''}`}
                            onClick={() => setConfig({ ...config, plantilla_base: 'MODERNA' })}
                        >
                            <div className="template-preview t-modern"></div>
                            <span>Moderna</span>
                        </div>
                    </div>
                </section>

                <section className="designer-section">
                    <h3><FiDroplet /> Decoraciones de Página</h3>
                    <div className="design-pattern-grid">
                        <div 
                            className={`pattern-option ${config.diseno_pagina === 'NINGUNO' ? 'active' : ''}`}
                            onClick={() => setConfig({ ...config, diseno_pagina: 'NINGUNO' })}
                        >
                            <span>Sin Diseño</span>
                        </div>
                        <div 
                            className={`pattern-option ${config.diseno_pagina === 'ONDAS' ? 'active' : ''}`}
                            onClick={() => setConfig({ ...config, diseno_pagina: 'ONDAS' })}
                        >
                            <span>Ondas</span>
                        </div>
                        <div 
                            className={`pattern-option ${config.diseno_pagina === 'GEOMETRICO' ? 'active' : ''}`}
                            onClick={() => setConfig({ ...config, diseno_pagina: 'GEOMETRICO' })}
                        >
                            <span>Geométrico</span>
                        </div>
                        <div 
                            className={`pattern-option ${config.diseno_pagina === 'MINIMALISTA' ? 'active' : ''}`}
                            onClick={() => setConfig({ ...config, diseno_pagina: 'MINIMALISTA' })}
                        >
                            <span>Líneas</span>
                        </div>
                    </div>
                </section>

                <section className="designer-section">
                    <h3><FiType /> Colores de Marca</h3>
                    <div className="color-inputs">
                        <div className="color-field">
                            <label>Color Primario</label>
                            <div className="color-picker-wrapper">
                                <input
                                    type="color"
                                    name="color_primario"
                                    value={config.color_primario}
                                    onChange={handleChange}
                                />
                                <input
                                    type="text"
                                    value={config.color_primario}
                                    onChange={(e) => setConfig({ ...config, color_primario: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="color-field">
                            <label>Color Secundario</label>
                            <div className="color-picker-wrapper">
                                <input
                                    type="color"
                                    name="color_secundario"
                                    value={config.color_secundario}
                                    onChange={handleChange}
                                />
                                <input
                                    type="text"
                                    value={config.color_secundario}
                                    onChange={(e) => setConfig({ ...config, color_secundario: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="color-field">
                            <label>Color de Texto</label>
                            <div className="color-picker-wrapper">
                                <input
                                    type="color"
                                    name="color_texto"
                                    value={config.color_texto}
                                    onChange={handleChange}
                                />
                                <input
                                    type="text"
                                    value={config.color_texto}
                                    onChange={(e) => setConfig({ ...config, color_texto: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>
                </section>

                <section className="designer-section">
                    <h3><FiImage /> Logo de Empresa</h3>
                    <div className="alignment-selector">
                        <label>Alineación</label>
                        <div className="btn-group-custom">
                            <button
                                type="button"
                                className={config.alineacion_logo === 'LEFT' ? 'active' : ''}
                                onClick={() => setConfig({ ...config, alineacion_logo: 'LEFT' })}
                            >Izq.</button>
                            <button
                                type="button"
                                className={config.alineacion_logo === 'CENTER' ? 'active' : ''}
                                onClick={() => setConfig({ ...config, alineacion_logo: 'CENTER' })}
                            >Centro</button>
                            <button
                                type="button"
                                className={config.alineacion_logo === 'RIGHT' ? 'active' : ''}
                                onClick={() => setConfig({ ...config, alineacion_logo: 'RIGHT' })}
                            >Der.</button>
                        </div>
                    </div>
                    <div className="size-selector">
                        <label>Tamaño</label>
                        <select name="tamano_logo" value={config.tamano_logo} onChange={handleChange}>
                            <option value="SMALL">Pequeño</option>
                            <option value="MEDIUM">Mediano</option>
                            <option value="LARGE">Grande</option>
                        </select>
                    </div>
                </section>

                <section className="designer-section">
                    <h3><FiCheckCircle /> Elementos Adicionales</h3>
                    <div className="checkbox-field">
                        <label>
                            <input
                                type="checkbox"
                                name="mostrar_firma"
                                checked={config.mostrar_firma}
                                onChange={handleChange}
                            />
                            Mostrar línea de firma
                        </label>
                    </div>
                    <div className="checkbox-field">
                        <label>
                            <input
                                type="checkbox"
                                name="mostrar_branding"
                                checked={config.mostrar_branding}
                                onChange={handleChange}
                            />
                            Mostrar branding "CotizaPlus"
                            <span className="hint-text">(Opcional para planes pagados)</span>
                        </label>
                    </div>
                    <div className="input-field">
                        <label>Texto Pie de Página</label>
                        <input
                            type="text"
                            name="texto_footer"
                            value={config.texto_footer}
                            onChange={handleChange}
                            placeholder="Ej: Gracias por confiar en nosotros"
                        />
                    </div>
                </section>

                <section className="designer-section">
                    <h3><FiFileText /> Contenido del Documento</h3>
                    <div className="input-field">
                        <label>Título Principal</label>
                        <input
                            type="text"
                            name="titulo_documento"
                            value={config.titulo_documento}
                            onChange={handleChange}
                            placeholder="Ej: PRESUPUESTO"
                        />
                    </div>
                    <div className="size-selector">
                        <label>Tipografía (Fuente)</label>
                        <select name="fuente" value={config.fuente} onChange={handleChange}>
                            <option value="HELVETICA">Sans Serif (Helvetica)</option>
                            <option value="TIMES">Serif (Times New Roman)</option>
                            <option value="COURIER">Moderna (Courier)</option>
                        </select>
                    </div>
                </section>

                <section className="designer-section">
                    <h3><FiColumns /> Tabla y Columnas</h3>
                    <div className="checkbox-group">
                        <label className="checkbox-field">
                            <input
                                type="checkbox"
                                name="mostrar_sku"
                                checked={config.mostrar_sku}
                                onChange={handleChange}
                            />
                            Mostrar SKU/Código
                        </label>
                        <label className="checkbox-field">
                            <input
                                type="checkbox"
                                name="mostrar_impuesto_col"
                                checked={config.mostrar_impuesto_col}
                                onChange={handleChange}
                            />
                            Mostrar Columna Impuesto
                        </label>
                        <label className="checkbox-field">
                            <input
                                type="checkbox"
                                name="mostrar_label_oferta"
                                checked={config.mostrar_label_oferta}
                                onChange={handleChange}
                            />
                            Habilitar label "Oferta" en productos
                        </label>
                    </div>
                    <div className="size-selector" style={{ marginTop: '12px' }}>
                        <label>Estilo de Tabla</label>
                        <select name="estilo_tabla" value={config.estilo_tabla} onChange={handleChange}>
                            <option value="ESTANDAR">Estándar (Bordes completos)</option>
                            <option value="MINIMALISTA">Minimalista (Limpio)</option>
                            <option value="MODERN">Cuadrícula Moderna</option>
                        </select>
                    </div>
                </section>

                <section className="designer-section">
                    <h3><FiCreditCard /> Pago y Notas Legales</h3>
                    <div className="input-field">
                        <label>Datos Bancarios (Transferencia)</label>
                        <textarea
                            name="datos_bancarios"
                            value={config.datos_bancarios}
                            onChange={handleChange}
                            placeholder="Banco, Cuenta, RUT..."
                            rows="3"
                        ></textarea>
                    </div>
                    <div className="input-field">
                        <label>Términos y Condiciones</label>
                        <textarea
                            name="terminos_condiciones"
                            value={config.terminos_condiciones}
                            onChange={handleChange}
                            placeholder="Garantías, validez, etc."
                            rows="3"
                        ></textarea>
                    </div>
                </section>

                <section className="designer-section qr-config-section">
                    <h3><FiColumns /> Configuración Código QR</h3>
                    <div className="qr-selection-grid">
                        <div 
                            className={`qr-option-card ${config.mostrar_qr_virtual ? 'active' : ''}`}
                            onClick={() => setConfig(prev => ({ ...prev, mostrar_qr_virtual: true, qr_contenido: '' }))}
                        >
                            <input
                                type="radio"
                                checked={config.mostrar_qr_virtual}
                                readOnly
                            />
                            <div className="qr-option-content">
                                <strong>Agregar QR descarga cotización</strong>
                                <span>Genera un link automático a la versión digital.</span>
                            </div>
                        </div>

                        <div 
                            className={`qr-option-card ${!config.mostrar_qr_virtual ? 'active' : ''}`}
                            onClick={() => setConfig(prev => ({ ...prev, mostrar_qr_virtual: false }))}
                        >
                            <input
                                type="radio"
                                checked={!config.mostrar_qr_virtual}
                                readOnly
                            />
                            <div className="qr-option-content">
                                <strong>Contenido personalizado</strong>
                                <span>Ingresa tu propio link (pago, web, etc).</span>
                            </div>
                        </div>
                    </div>
                    
                    {!config.mostrar_qr_virtual && (
                        <div className="form-group animate-fade-in" style={{ marginTop: '1rem' }}>
                            <label>URL o Texto del QR</label>
                            <input
                                type="text"
                                value={config.qr_contenido}
                                onChange={(e) => setConfig(prev => ({ ...prev, qr_contenido: e.target.value }))}
                                placeholder="Ej: https://misitio.com/pago"
                            />
                        </div>
                    )}

                    <div className="form-group" style={{ marginTop: '1rem' }}>
                        <label>Descripción debajo del QR</label>
                        <input
                            type="text"
                            value={config.qr_descripcion}
                            onChange={(e) => setConfig(prev => ({ ...prev, qr_descripcion: e.target.value }))}
                            placeholder="Ej: Escanea para descargar cotización"
                            maxLength="100"
                        />
                        <p className="input-info">
                            Texto pequeño que aparecerá justo debajo del QR en el PDF.
                        </p>
                    </div>
                </section>

                <section className="designer-section">
                    <h3><FiGlobe /> Contacto y Extras</h3>
                    <div className="input-field">
                        <label><FiInstagram /> Instagram (@usuario)</label>
                        <input
                            type="text"
                            name="instagram"
                            value={config.instagram}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="input-field">
                        <label><FiGlobe /> Sitio Web</label>
                        <input
                            type="text"
                            name="web"
                            value={config.web}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="input-field">
                        <label>Marca de Agua (Texto central)</label>
                        <input
                            type="text"
                            name="marca_agua"
                            value={config.marca_agua}
                            onChange={handleChange}
                            placeholder="Ej: CONFIDENCIAL"
                        />
                    </div>
                </section>

                <button
                    className="btn-custom btn-primary btn-save-design"
                    onClick={handleSave}
                    disabled={saving}
                >
                    <FiSave /> {saving ? 'Guardando...' : 'Guardar Diseño'}
                </button>
            </div>

            <div className="designer-preview">
                <div className="preview-header">
                    <span><FiEye /> Vista Previa Real</span>
                    {previewLoading && <span className="preview-status">Actualizando...</span>}
                </div>
                <div className="preview-canvas">
                    {error ? (
                        <div className="preview-error">
                            <p>{error}</p>
                            <button onClick={() => generatePreview(config)} className="btn-retry">Reintentar</button>
                        </div>
                    ) : previewUrl ? (
                        <iframe src={previewUrl} title="Cotización Preview" />
                    ) : (
                        <div className="preview-placeholder">
                            <p>Generando vista previa...</p>
                        </div>
                    )}
                </div>
                <p className="preview-tip">
                    * La vista previa usa los datos de tu última cotización real como ejemplo.
                </p>
            </div>
        </div>
    );
};

export default QuotationDesigner;
